import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/services/api/apiRequest";
import { getConfig } from "@/services/api/config";
import { getClerkToken } from "@/services/clerk";
import type { MemberProfile, ProfileUpdate } from "../members.types";

function useInvalidateMe() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["afell"] });
}

export function useUpdateProfile() {
  const invalidate = useInvalidateMe();
  return useMutation<MemberProfile, Error, ProfileUpdate>({
    mutationFn: (payload) => apiRequest("/afell/profile/", "POST", payload),
    onSuccess: invalidate,
  });
}

/** Multipart upload — bypasses apiRequest's JSON encoding. */
export function useSetAvatar() {
  const invalidate = useInvalidateMe();
  return useMutation<{ image_url: string }, Error, File>({
    mutationFn: async (file) => {
      const body = new FormData();
      body.append("image", file);
      const headers: Record<string, string> = {};
      const token = await getClerkToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const response = await fetch(`${getConfig().apiUrl}/afell/profile/avatar/`, {
        method: "POST",
        headers,
        body,
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Upload failed");
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useRemoveAvatar() {
  const invalidate = useInvalidateMe();
  return useMutation({
    mutationFn: () => apiRequest("/afell/profile/avatar/", "DELETE"),
    onSuccess: invalidate,
  });
}
